import React, { useState, useCallback } from 'react';
import { Button } from '@mui/material';
import { useDispatch } from 'react-redux';
import UIUtils from 'js/lib/UIUtils';
import Constants from 'js/Constants';
import { uploadFileToSource } from 'js/actions';
import { useFileUploadTab } from 'js/components/data_sources/FileUploadTab/useFileUploadTab';
import { FileUploadConfiguration } from 'js/components/data_sources/add_ds_form/FileUploadConfiguration/FileUploadConfiguration';
import { FileWithPreview } from 'js/components/data_sources/add_ds_form/FileUploadConfiguration/FileUploadConfiguration.types';

import './FileUploadTab.scss';
import { useGetPaging } from 'js/hooks/useGetPaging';

type Props = {
  sourceID: number;
};

type ResponseStatus = { title: string; message: string };

const ErrorStatus = {
  title: 'Sorry, something went wrong to updated the data source.',
  message: 'An unexpected error has occurred. Please try again later.',
};

const SuccessStatus = {
  title: 'You have successfully uploaded your file(s) to this source.',
  message: `Your file(s) are being scanned for processing. Please give a few minutes for metrics to start reflecting processing status.`,
};

export function FileUploadTab({ sourceID }: Props) {
  const {
    selectedFile,
    setSelectedFile,
    source,
    currentOrg,
    selectedConnection,
    sourceConfiguration,
    setSourceConfiguration,
  } = useFileUploadTab({ sourceID });
  const paging = useGetPaging();
  const dispatch = useDispatch();
  const [sourceName, setSourceName] = useState('');
  const [status, setStatus] = useState({} as ResponseStatus);
  const [selectedFiles, setSelectedFiles] = useState<FileWithPreview[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState<Set<string>>(new Set());

  if (!source) return null;

  function onResetForm() {
    setSourceName('');
    setSelectedFile(null);
    setSelectedFiles([]);
    setStatus({} as ResponseStatus);
    setUploadingFiles(new Set());
  }

  function onMetaInfoChanged(name: string, value: string) {
    if (name === 'name') {
      setSourceName(value);
    }
  }

  const handleFilesSelected = useCallback(
    (files: File[]) => {
      const filesWithPreview: FileWithPreview[] = files.map(
        (file) =>
          Object.assign(file, {
            id: `${file.name}-${file.size}-${file.lastModified}`,
            status: 'pending' as const,
            uploadProgress: 0,
            backendUploadProgress: 0,
          }) as FileWithPreview,
      );

      // Append new files to existing ones, filtering out duplicates by id
      setSelectedFiles((prev) => {
        const existingIds = new Set(prev.map((f) => f.id));
        const newFiles = filesWithPreview.filter((f) => !existingIds.has(f.id));
        return [...prev, ...newFiles];
      });

      // For backward compatibility, also set selectedFile
      if (files.length > 0) {
        setSelectedFile(files[0]);
      }
    },
    [setSelectedFile],
  );

  const handleFileSelected = useCallback(
    (file: File | null) => {
      if (file) {
        const fileWithPreview = Object.assign(file, {
          id: `${file.name}-${file.size}-${file.lastModified}`,
          status: 'pending' as const,
          uploadProgress: 0,
          backendUploadProgress: 0,
        }) as FileWithPreview;

        // Only override selectedFiles when we are in a single-file flow.
        setSelectedFiles((prev) => {
          if (prev.length > 1) {
            return prev;
          }
          return [fileWithPreview];
        });

        setSelectedFile(file);
      } else {
        setSelectedFiles([]);
        setSelectedFile(null);
      }
    },
    [setSelectedFile],
  );

  const buildSourceConfigFromProps = () => {
    const { sourceConfigById } = paging as any;

    const configFields = sourceConfigById[selectedConnection];
    let dataSourceConfig: { [x: string]: any };

    dataSourceConfig = UIUtils.configWithMissingDefaults(sourceConfiguration, configFields);

    const includes = ({ name }) => Constants.HC_SOURCE_FIELD_NAMES.includes(name);
    const handledFields = configFields.filter(includes);

    if (UIUtils.isHCEnabled(currentOrg.id, selectedConnection)) {
      dataSourceConfig = UIUtils.configWithMissingDefaults(dataSourceConfig, handledFields);

      handledFields.forEach((field: { name: string | number }) => {
        if (!UIUtils.shouldRenderField(field, dataSourceConfig)) {
          delete dataSourceConfig[field.name];
        }
      });
    } else {
      const toDelete = ({ name }) => delete dataSourceConfig[name];
      handledFields.forEach(toDelete);
    }

    return dataSourceConfig;
  };

  const logUploadEvent = useCallback(
    (fileId: string, message: string, extra?: Record<string, unknown>) => {
      // eslint-disable-next-line no-console
      console.info('[file-upload]', { fileId, message, ...extra });
    },
    [],
  );

  const updateFileProgress = useCallback(
    (fileId: string, progress: number, type: 'upload' | 'backend') => {
      logUploadEvent(fileId, 'progress', { type, progress });
      setSelectedFiles((prev) =>
        prev.map((file) => {
          if (file.id !== fileId) return file;

          const nextUpload = type === 'upload' ? progress : (file.uploadProgress ?? 0);
          const nextBackend = type === 'backend' ? progress : (file.backendUploadProgress ?? 0);
          const isComplete = nextUpload === 100 && nextBackend === 100;

          return {
            ...file,
            uploadProgress: nextUpload,
            backendUploadProgress: nextBackend,
            status: isComplete ? 'uploaded' : 'uploading',
          };
        }),
      );
    },
    [logUploadEvent],
  );

  /**
   * Simulates gradual progress for better UX
   */
  const simulateProgress = useCallback(
    (fileId: string, type: 'upload' | 'backend', duration: number = 1500): Promise<void> => {
      return new Promise((resolve) => {
        const startTime = Date.now();
        const targetProgress = type === 'upload' ? 95 : 90; // Leave room for actual completion

        const updateProgress = () => {
          const elapsed = Date.now() - startTime;
          const progress = Math.min(
            Math.round((elapsed / duration) * targetProgress),
            targetProgress,
          );

          updateFileProgress(fileId, progress, type);

          if (progress < targetProgress) {
            requestAnimationFrame(updateProgress);
          } else {
            resolve();
          }
        };

        requestAnimationFrame(updateProgress);
      });
    },
    [updateFileProgress],
  );

  const _onRequestSourceCreation = async () => {
    if (!sourceID) return;

    const filesToUpload =
      selectedFiles.length > 0
        ? selectedFiles
        : selectedFile
          ? [
              Object.assign(selectedFile, {
                id: `${selectedFile.name}-${selectedFile.size}-${Date.now()}`,
                status: 'pending' as const,
              }) as FileWithPreview,
            ]
          : [];

    if (filesToUpload.length === 0) {
      return;
    }

    // Mark all files as uploading
    setSelectedFiles((prev) =>
      prev.map((f) =>
        filesToUpload.some((ftf) => ftf.id === f.id) ? { ...f, status: 'uploading' as const } : f,
      ),
    );

    setUploadingFiles(new Set(filesToUpload.map((f) => f.id)));

    try {
      const runUpload = async (fileWithPreview: FileWithPreview) => {
        const file = fileWithPreview as File;

        logUploadEvent(fileWithPreview.id, 'start-upload', {
          name: file.name,
          size: file.size,
        });

        // Simulate browser upload progress while real upload runs
        const uploadSim = simulateProgress(fileWithPreview.id, 'upload', 800);
        const uploadAction = uploadFileToSource(sourceID, file);
        const uploadPromise = dispatch<any>(uploadAction);

        await uploadSim;
        updateFileProgress(fileWithPreview.id, 100, 'upload');

        // Simulate backend processing while awaiting upload result
        const backendSim = simulateProgress(fileWithPreview.id, 'backend', 1200);
        const { result: upload } = await uploadPromise;
        await backendSim;
        updateFileProgress(fileWithPreview.id, 100, 'backend');

        const isSuccessCase = upload?.message?.toLowerCase?.() === 'success';
        if (!isSuccessCase) {
          setSelectedFiles((prev) =>
            prev.map((f) => (f.id === fileWithPreview.id ? { ...f, status: 'error' as const } : f)),
          );
          logUploadEvent(fileWithPreview.id, 'upload-failed', { response: upload });
          throw new Error('Upload failed');
        }

        logUploadEvent(fileWithPreview.id, 'upload-complete');
      };

      const results = await Promise.allSettled(filesToUpload.map(runUpload));

      // Small delay to show completion state
      await new Promise((resolve) => setTimeout(resolve, 500));

      const anyFailed = results.some((r) => r.status === 'rejected');
      setStatus(anyFailed ? ErrorStatus : SuccessStatus);
    } catch (error) {
      setStatus(ErrorStatus);
      // Mark all uploading files as error
      setSelectedFiles((prev) =>
        prev.map((f) => (f.status === 'uploading' ? { ...f, status: 'error' as const } : f)),
      );
      console.error('_onRequestSourceCreation:', error);
    } finally {
      setUploadingFiles(new Set());
    }
  };

  if (Object.values(status).length) {
    return (
      <div className="file-upload-tab-container">
        <div className="message-container">
          <span className="message">{status.title}</span>
          <p className="description">{status.message}</p>
        </div>
        <br />
        <Button variant="contained" onClick={onResetForm}>
          Upload New File(s)
        </Button>
      </div>
    );
  }

  const hasFiles = selectedFiles.length > 0 || selectedFile !== null;

  return (
    <div className="file-upload-tab-container">
      <FileUploadConfiguration
        enableCategoryHeader={false}
        disableConfiguration
        name={sourceName}
        selectedFile={selectedFile}
        selectedFiles={selectedFiles}
        sourceConfig={sourceConfiguration}
        selectedConnection={selectedConnection}
        onFileSelected={handleFileSelected}
        onFilesSelected={handleFilesSelected}
        onMetaInfoChanged={onMetaInfoChanged}
        onSourceConfigChanged={setSourceConfiguration}
        allowMultiple={true}
        showPreview={true}
      />

      <div className="save-button-container">
        <Button
          disabled={!hasFiles || uploadingFiles.size > 0}
          variant="contained"
          onClick={_onRequestSourceCreation}
        >
          {uploadingFiles.size > 0
            ? `Uploading ${uploadingFiles.size} file(s)...`
            : selectedFiles.length > 1
              ? `Upload ${selectedFiles.length} Files`
              : 'Upload File'}
        </Button>
      </div>
    </div>
  );
}
